import React from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import Timeline from '@material-ui/lab/Timeline';
import TimelineItem from '@material-ui/lab/TimelineItem';
import TimelineSeparator from '@material-ui/lab/TimelineSeparator';
import TimelineConnector from '@material-ui/lab/TimelineConnector';
import TimelineContent from '@material-ui/lab/TimelineContent';
import TimelineOppositeContent from '@material-ui/lab/TimelineOppositeContent';
import TimelineDot from '@material-ui/lab/TimelineDot';
import CreateIcon from '@material-ui/icons/NoteAdd';
import PayIcon from '@material-ui/icons/CreditCard';
import TransferIcon from '@material-ui/icons/AccountBalanceWallet';
import PayoutIcon from '@material-ui/icons/AccountBalance';
import CancelIcon from '@material-ui/icons/Cancel';
import RefundIcon from '@material-ui/icons/Undo';
import CheckinIcon from '@material-ui/icons/Hotel';
import CheckoutIcon from '@material-ui/icons/ExitToApp';
import Typography from '@material-ui/core/Typography';
import Box from '@material-ui/core/Box';
import { FormattedNumber, FormattedMessage, useIntl } from 'react-intl';
import moment from 'logic/moment';

const styles = makeStyles((theme) => ({
  'bg.primary': {
    backgroundColor: theme.palette.primary.main,
  },
  'bg.secondary': {
    backgroundColor: theme.palette.secondary.light,
  },
  'bg.success': {
    backgroundColor: theme.palette.success.light,
  },
  'bg.gray': {
    backgroundColor: theme.palette.grey.light,
  },
  contentBox: {
    padding: theme.spacing(1),

    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(1),
    },
  },
  content: {
    fontSize: '0.9rem',
    wordBreak: 'break-word',

    [theme.breakpoints.down('sm')]: {
      fontSize: '0.8rem',
    },
  },
  contentOpposite: {
    fontSize: '0.9rem',
    paddingTop: '6px',
    wordBreak: 'break-word',

    [theme.breakpoints.down('sm')]: {
      fontSize: '0.8rem',
    },
  },
}));

const colors = {
  NEUTRAL: 'bg.primary',
  ERROR: 'bg.secondary',
  SUCCESS: 'bg.success',
  FUTURE: 'bg.gray',
};

const types = {
  CREATED: (event) => ({
    body: (
      <FormattedMessage
        id="booking.timeline.created.body"
        defaultMessage="{user} created a booking offer"
        values={{
          user: event.user,
        }}
      />
    ),
    icon: (<CreateIcon />),
  }),
  CANCELLED: (event) => ({
    body: (
      <FormattedMessage
        id="booking.timeline.cancelled.body"
        defaultMessage="The booking offer was cancelled by {user}"
        values={{
          user: event.user,
        }}
      />
    ),
    icon: (<CancelIcon />),
  }),
  REFUNDED: () => ({
    body: (
      <FormattedMessage
        id="booking.timeline.refunded.body"
        defaultMessage="The booking was refunded"
      />
    ),
    icon: (<RefundIcon />),
  }),
  FULL_PAYMENT_DUE_DATE: () => ({
    body: (
      <FormattedMessage
        id="booking.timeline.payment.duedate.body"
        defaultMessage="Final payment due date"
      />
    ),
    icon: (<PayIcon />),
  }),
  FULL_PAYMENT: (event) => ({
    body: (
      <FormattedMessage
        id="booking.timeline.paid.body"
        defaultMessage="{user} paid the total price of {amount} SEK"
        values={{
          user: event.user,
          amount: (<FormattedNumber value={event.amount} />),
        }}
      />
    ),
    icon: (<PayIcon />),
  }),
  TRANSFER: () => ({
    body: (
      <FormattedMessage
        id="booking.timeline.transfered.body"
        defaultMessage="The booking total price was transfered to the host wallet"
      />
    ),
    icon: (<TransferIcon />),
  }),
  PAY_OUT: () => ({
    body: (
      <>
        <FormattedMessage
          id="booking.timeline.paidout.body"
          defaultMessage="Payout registered"
        />
        <hr />
        <FormattedMessage
          id="booking.timeline.paidout.bankinfo.body"
          defaultMessage="(2-5 banking days)"
        />
      </>
    ),
    icon: (<PayoutIcon />),
  }),
  PAY_OUT_PENDING: () => ({
    body: (
      <FormattedMessage
        id="booking.timeline.waiting.paidout.body"
        defaultMessage="Payout being processed"
      />
    ),
    icon: (<PayoutIcon />),
  }),
  PAY_OUT_DUE_DATE: () => ({
    body: (
      <FormattedMessage
        id="booking.timeline.earliest.paidout.body"
        defaultMessage="Payout process begins earliest at"
      />
    ),
    icon: (<PayoutIcon />),
  }),
  CHECKIN: () => ({
    body: (
      <FormattedMessage
        id="booking.timeline.checkin.body"
        defaultMessage="Check in"
      />
    ),
    icon: (<CheckinIcon />),
  }),
  CHECKOUT: () => ({
    body: (
      <FormattedMessage
        id="booking.timeline.checkout.body"
        defaultMessage="Check out"
      />
    ),
    icon: (<CheckoutIcon />),
  }),
};

const EventsTimeline = (props) => {
  const classes = styles();
  const intl = useIntl();
  const events = props.booking.Timeline;

  if (!events || !events.length) {
    return null;
  }

  return (
    <Box displayPrint="none">
      <Timeline align="alternate">
        {
          events.map((event, i) => {
            const { date } = event;
            let { user } = event;

            if (user && user === 'Deleted') {
              user = intl.formatMessage({ id: 'Deleted', defaultMessage: 'Deleted' });
            }

            const bg = colors[event.state];
            const { body, icon } = types[event.type]({
              ...event,
              user,
            });

            return (
              <TimelineItem key={i}>
                <TimelineOppositeContent>
                  <Typography variant="body2" color="textSecondary" className={classes.contentOpposite}>
                    {moment(date).format('LLL')}
                  </Typography>
                </TimelineOppositeContent>
                <TimelineSeparator>
                  <TimelineDot className={classes[bg]}>
                    {icon}
                  </TimelineDot>
                  {
                    i < events.length - 1 && <TimelineConnector />
                  }
                </TimelineSeparator>
                <TimelineContent>
                  <Box elevation={3} boxShadow={3} textAlign="center" className={classes.contentBox}>
                    <Typography className={classes.content}>{body}</Typography>
                  </Box>
                </TimelineContent>
              </TimelineItem>
            );
          })
        }
      </Timeline>
    </Box>

  );
};

EventsTimeline.propTypes = {
  booking: PropTypes.shape({
    Timeline: PropTypes.arrayOf(PropTypes.shape({})),
    paid_at: PropTypes.string,
    paid_out_at: PropTypes.string,
    transfered_at: PropTypes.string,
    check_in: PropTypes.string,
    check_out: PropTypes.string,
    Host: PropTypes.shape({
      name: PropTypes.string,
    }).isRequired,
    Guest: PropTypes.shape({
      name: PropTypes.string,
    }),
    refunded_at: PropTypes.string,
    cancelled_at: PropTypes.string,
    created_at: PropTypes.string,
  }).isRequired,
};

export default EventsTimeline;
